/**
 * /reset-password?token=…: where the "Esqueci a senha" email lands (docs/refactor/65). The new
 * password, twice; then back to the sign-in page, which says the password was changed. A link that
 * no longer works says so and offers to ask for another — every refusal reads the same, by design.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import Logo from '../components/Logo'

export default function ResetPassword() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(!token)

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.auth.resetPassword(token, password)
      navigate('/sign-in', { replace: true, state: { notice: 'passwordReset' } })
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'invalid_reset_token') setExpired(true)
      else if (caught instanceof ApiError && caught.status === 409) setError(caught.message)
      else setError(t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <Logo size={36} />
          <div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 650, letterSpacing: '0.08em' }}>VEPHUS</div>
            <div className="auth__tagline">{t('auth.reset.title')}</div>
          </div>
        </div>

        {expired ? (
          <div className="stack">
            <p className="small" role="alert" style={{ margin: 0 }}>{t('auth.reset.expired')}</p>
            <Link className="btn btn--primary full" to="/sign-in" state={{ forgot: true }}>{t('auth.reset.askAgain')}</Link>
          </div>
        ) : (
          <form className="stack" onSubmit={(event) => void submit(event)}>
            <p className="small muted" style={{ margin: 0 }}>{t('auth.reset.body')}</p>
            <label className="field">
              <span className="field__label">{t('auth.newPassword')}</span>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} autoComplete="new-password" autoFocus />
              <span className="field__hint">{t('auth.passwordHint')}</span>
            </label>
            <label className="field">
              <span className="field__label">{t('auth.confirmPassword')}</span>
              <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={12} autoComplete="new-password" />
            </label>
            {error && <div className="field__error" role="alert">{error}</div>}
            <button type="submit" className="btn btn--primary full" disabled={busy}>
              {busy ? t('auth.reset.saving') : t('auth.reset.submit')}
            </button>
          </form>
        )}

        <p className="tiny muted signin__version">
          <Link to="/sign-in">{t('auth.signIn')}</Link> · <Link to="/privacy">{t('privacy.title')}</Link>
        </p>
      </div>
    </div>
  )
}
